import React from 'react'
import ReactDOM from 'react-dom'
import App from './App'
import * as serviceWorker from './serviceWorker'
import theme from 'ui-components/lib/theme'
import { ThemeProvider } from 'styled-components'
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext'
import { getTheme } from './themes'
import 'typeface-lato'
import GlobalStyles from './utils/GlobalStyles'
import AlertStackProvider from 'ui-components/lib/components/AlertStack/AlertStackProvider'
import AlertStack from 'ui-components/lib/components/AlertStack'

// Remove iframe indesejado criado pelo Webpack Dev Server ou ferramentas de desenvolvimento
// Este iframe aparece apenas em modo desenvolvimento (npm start) e não em produção (npm run build)
(function() {  
  let isInitialized = false
  
  const isUnwantedIframe = (element) => {
    if (!element || element.tagName !== 'IFRAME') {
      return false
    }
    
    try {
      const style = element.getAttribute('style') || ''
      const computedStyle = window.getComputedStyle ? window.getComputedStyle(element) : null
      
      // Verifica pelo atributo style diretamente (mais rápido)
      if (style.includes('z-index: 2147483647') || style.includes('z-index:2147483647')) {
        if (
          style.includes('position: fixed') &&
          style.includes('width: 100%') &&
          style.includes('height: 100%')
        ) {
          return true
        }
      }
      
      // Verifica pelo computed style como fallback
      if (computedStyle) {
        const zIndex = computedStyle.getPropertyValue('z-index')
        if (zIndex === '2147483647' || zIndex === '2147483646') {
          if (
            computedStyle.getPropertyValue('position') === 'fixed' &&
            computedStyle.getPropertyValue('top') === '0px' &&
            computedStyle.getPropertyValue('left') === '0px' &&
            computedStyle.getPropertyValue('width') === '100%' &&
            computedStyle.getPropertyValue('height') === '100%' &&
            computedStyle.getPropertyValue('border') === 'none'
          ) {
            return true
          }
        }
      }
    } catch (e) {
      // Ignora erros
    }
    
    return false
  }

  const removeIframe = (iframe) => {
    try {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
        console.log('Iframe indesejado removido')
        return true
      }
    } catch (e) {
      // Ignora erros
    }
    return false
  }

  const checkAndRemoveAll = () => {
    const iframes = document.querySelectorAll('iframe')
    let removed = false
    iframes.forEach((iframe) => {
      if (isUnwantedIframe(iframe)) {
        if (removeIframe(iframe)) {
          removed = true
        }
      }
    })
    return removed
  }

  // Intercepta appendChild para remover imediatamente após adicionar
  const interceptAppendChild = () => {
    const originalAppendChild = Node.prototype.appendChild
    Node.prototype.appendChild = function(child) {
      const result = originalAppendChild.call(this, child)
      if (isUnwantedIframe(child)) {
        setTimeout(() => {
          if (child.parentNode) {
            removeIframe(child)
          }
        }, 0)
      }
      return result
    }
  }

  // Intercepta insertBefore para remover imediatamente após inserir
  const interceptInsertBefore = () => {
    const originalInsertBefore = Node.prototype.insertBefore
    Node.prototype.insertBefore = function(newNode, referenceNode) {
      const result = originalInsertBefore.call(this, newNode, referenceNode)
      if (isUnwantedIframe(newNode)) {
        setTimeout(() => {
          if (newNode.parentNode) {
            removeIframe(newNode)
          }
        }, 0)
      }
      return result
    }
  }

  // Intercepta createElement para modificar iframes antes de serem criados
  const interceptCreateElement = () => {
    const originalCreateElement = Document.prototype.createElement
    Document.prototype.createElement = function(tagName, options) {
      const element = originalCreateElement.call(this, tagName, options)
      
      if (tagName.toLowerCase() === 'iframe') {
        // Monitora mudanças no style do iframe
        const originalSetAttribute = element.setAttribute
        element.setAttribute = function(name, value) {
          originalSetAttribute.call(this, name, value)
          if (name === 'style' && isUnwantedIframe(this)) {
            setTimeout(() => removeIframe(this), 0)
          }
        }
        
        // Monitora mudanças via style property
        if (element.style) {
          const styleProxy = new Proxy(element.style, {
            set: function(target, property, value) {
              target[property] = value
              if (isUnwantedIframe(element)) {
                setTimeout(() => removeIframe(element), 0)
              }
              return true
            }
          })
          Object.defineProperty(element, 'style', {
            get: () => styleProxy,
            set: () => {}
          })
        }
      }
      
      return element
    }
  }

  const init = () => {
    if (isInitialized) return
    isInitialized = true

    // Intercepta métodos de criação/inserção
    interceptAppendChild()
    interceptInsertBefore()
    interceptCreateElement()

    // Verifica imediatamente
    checkAndRemoveAll()

    // Verificação muito frequente (a cada 100ms)
    const intervalId = setInterval(() => {
      checkAndRemoveAll()
    }, 100)

    // MutationObserver com configuração mais agressiva
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IFRAME' || node.querySelectorAll) {
              shouldCheck = true
            }
          }
        })
      })
      if (shouldCheck) {
        setTimeout(checkAndRemoveAll, 0)
      }
    })

    const startObserver = () => {
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style']
        })
      }
      if (document.documentElement) {
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style']
        })
      }
    }

    if (document.body && document.documentElement) {
      startObserver()
    } else {
      const checkReady = setInterval(() => {
        if (document.body && document.documentElement) {
          startObserver()
          checkAndRemoveAll()
          clearInterval(checkReady)
        }
      }, 50)
      setTimeout(() => clearInterval(checkReady), 5000)
    }

    // Limpa recursos ao descarregar
    window.addEventListener('beforeunload', () => {
      clearInterval(intervalId)
      observer.disconnect()
    })
  }

  // Executa o mais cedo possível
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  // Executa imediatamente também
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(init, 0)
  }
})()

const ThemedAppContent = () => {
  const { theme: currentTheme } = useTheme()
  const appTheme = getTheme(currentTheme)
  const mergedTheme = { ...theme, ...appTheme }

  return (
    <ThemeProvider theme={mergedTheme}>
      <AlertStackProvider>
        <App />
        <AlertStack />
      </AlertStackProvider>
    </ThemeProvider>
  )
}

const ThemedApp = () => {
  return (
    <CustomThemeProvider>
      <ThemedAppContent />
    </CustomThemeProvider>
  )
}

ReactDOM.render(
  <React.StrictMode>
    <GlobalStyles />
    <ThemedApp />
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
